import { useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { api, downloadFile, formatApiErrorDetail } from "../lib/api";
import DropZone from "../components/DropZone";
import { Button } from "../components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";

export default function PdfOcr() {
  const { t } = useTranslation();
  const [file, setFile] = useState(null);
  const [lang, setLang] = useState("spa+eng");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  async function run() {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("lang", lang);
      const { data } = await api.post("/pdf/ocr", fd);
      setResult(data);
      toast.success(t("success"));
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
    setBusy(false);
  }

  return (
    <div className="max-w-4xl mx-auto px-6 md:px-10 py-12" data-testid="pdf-ocr-page">
      <Link to="/tools/pdf" className="eyebrow text-zinc-500 hover:text-black">← {t("cta_back")}</Link>
      <h1 className="hero-h2 text-3xl sm:text-5xl mt-2">{t("tool_ocr")}</h1>
      <p className="text-zinc-500 mt-2">{t("tool_ocr_desc")}</p>

      <div className="mt-8">
        {!file ? (
          <DropZone accept="application/pdf" onFiles={(fs) => setFile(fs[0])} testId="ocr-dropzone" />
        ) : (
          <div className="border border-zinc-200 rounded-md p-6 bg-white space-y-5">
            <div className="hero-h2 text-xl">{file.name}</div>
            <div>
              <span className="eyebrow">{t("ocr_lang")}</span>
              <Select value={lang} onValueChange={setLang}>
                <SelectTrigger className="rounded-sm border-zinc-300 h-11 mt-1 w-60" data-testid="ocr-lang">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="spa+eng">Español + English</SelectItem>
                  <SelectItem value="spa">Español</SelectItem>
                  <SelectItem value="eng">English</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex gap-3">
              <Button onClick={run} disabled={busy}
                className="rounded-sm bg-black text-white hover:bg-zinc-800 h-11" data-testid="ocr-run">
                {busy ? t("processing") : `${t("cta_apply")} →`}
              </Button>
              <Button variant="ghost" className="rounded-sm h-11" onClick={() => { setFile(null); setResult(null); }}>
                {t("cta_clear")}
              </Button>
            </div>
          </div>
        )}
        {result && (
          <div className="mt-6 border border-black rounded-md p-6 bg-zinc-50 fade-up" data-testid="ocr-result">
            <span className="eyebrow">{t("result_ready")}</span>
            <div className="hero-h2 text-xl mt-1">{result.original_filename}</div>
            <Button onClick={() => downloadFile(result.id, result.original_filename)}
              className="rounded-sm bg-black text-white hover:bg-zinc-800 h-10 mt-4">
              ↓ {t("cta_download")}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
